import {
  ExpandLess,
  ExpandMore
} from '@mui/icons-material'
import React, { FC, useState } from 'react'
// import SyncIcon from '@/assets/icons/sync.svg'
import { Sync } from '@mui/icons-material'
import type { ListItemProps } from '@mui/material'
import {
  Alert,
  Box,
  Collapse,
  Divider,
  List,
  ListItem,
  Drawer as MuiDrawer,
  Snackbar,
  styled,
  Typography,
  useMediaQuery
} from '@mui/material'
import { Link, useLocation } from 'react-router-dom'

import { syncAllData } from '@/api/maintenance-tickets-api'
import { routes } from '@/routes/index'
import theme from '@/utilities/Theme'
import { useAlert } from '@/utilities/useAlert'

const DrawerContainer = styled(MuiDrawer)<{ open: boolean }>(({ open }) => ({
  width: open ? 300 : 0, // Set width to 300px when open, otherwise 10%
  flexShrink: 0,
  '& .MuiDrawer-paper': {
    paddingLeft: '1%',
    width: open ? 300 : 0,
    boxSizing: 'border-box',
    background: '#00000',
    color: '#000',
  },
  overflow: 'hidden',
}))

interface StyledListItemProps extends ListItemProps {
  isactive?: boolean
}

const StyledListItem = styled(({ isactive, ...props }: StyledListItemProps) => (
  <ListItem {...props} />
))(({ isactive }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: '10px 16px',
  color: isactive ? '#495AFF' : '#000000',
  fontWeight: isactive ? 'bold' : 'normal',
  position: 'relative',
  '&:hover': {
    color: '#495AFF',
    fontWeight: 'bold',
    '& img, & svg': {
      filter:
        'invert(33%) sepia(51%) saturate(2878%) hue-rotate(222deg) brightness(100%) contrast(80%)',
    },
  },
  '& img, & svg': {
    filter: isactive
      ? 'invert(33%) sepia(51%) saturate(2878%) hue-rotate(222deg) brightness(100%) contrast(80%)'
      : 'invert(0%)',
    color: '#495AFF',
    maxWidth: '24px',
    maxHeight: '24px',
  },
}))

const CircleDot = styled('div')(() => ({
  width: '8px',
  height: '8px',
  borderRadius: '50%',
  backgroundColor: '#495AFF',
  marginLeft: '8px',
}))

const DropdownIconWrapper = styled('div')(() => ({
  position: 'absolute',
  right: '16px',
  display: 'flex',
  alignItems: 'center',
  '& svg': {
    color: '#000000',
  },
}))

const IconWrapper = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  marginRight: '10px',
  width: '24px',
  height: '24px',
  '& img, & svg': {
    maxWidth: '100%',
    maxHeight: '100%',
    objectFit: 'contain',
  },
}))

const MenuStyles = styled('div')(() => ({
  display: 'flex',
  marginTop: '8%',
  flexDirection: 'column',
  justifyContent: 'center',
}))

const DrawerText = styled(Typography)(() => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  textAlign: 'center',
}))

const CustomDivider = styled(Divider)(() => ({
  marginTop: '8%',
  marginBottom: '-15%',
}))

interface DrawerProps {
  open: boolean
  onClose: () => void
}

export const Drawer: FC<DrawerProps> = ({ open, onClose }) => {
  const [openManagement, setOpenManagement] = useState(false)
  const location = useLocation()

  const roleString = localStorage.getItem('role')
  const userRole = roleString ? JSON.parse(roleString) : ''

  const specificRouteOrder = [
    routes.find((route) => route.name === 'Dashboard')!,
    routes.find((route) => route.name === 'Maintenance Tickets')!,
  ]

  const managementRoutes = routes.filter((route) =>
    ['Area Management', 'User Management'].includes(route.name),
  )

  const remainingRoutes = routes.filter((route) => {
    if (userRole === 'Super Admin') {
      return ['FAQs', 'Logout', 'Sync'].includes(route.name)
    } else {
      return ['FAQs', 'Inbox', 'Logout'].includes(route.name)
    }
  })
  const handleManagementClick = () => {
    setOpenManagement(!openManagement)
  }

  const { handleAlertMessage, ...alertRest } = useAlert()

  const handleSync = async () => {
    try {
      const response = await syncAllData()
      console.log('sync response', response.data.message)
      handleAlertMessage(response.data.message, 'success')
    } catch (e) {
      handleAlertMessage('Error syncing data.', 'error')
      console.error(e)
    }
  }

  return (
    <DrawerContainer
      variant={
        useMediaQuery(theme.breakpoints.down('md')) ? 'temporary' : 'persistent'
      }
      anchor="left"
      open={open}
      onClose={onClose}
    >
      <Box sx={{ m: 3 }}>
        <DrawerText variant="h6" fontFamily={'Gabarito'}>
          DEALER NETWORK DEVELOPMENT
        </DrawerText>
        <CustomDivider />
      </Box>
      <MenuStyles>
        {userRole !== 'Super Admin' &&
          specificRouteOrder.map((route, index) => (
            <Link
              to={route.path}
              key={route.path}
              style={{ textDecoration: 'none' }}
            >
              <StyledListItem isactive={location.pathname === route.path}>
                <IconWrapper>
                  <img src={route.icon} alt={`${route.name} icon`} />
                </IconWrapper>
                <Typography fontFamily={'Gabarito'}>{route.name}</Typography>
                {location.pathname === route.path && <CircleDot />}
              </StyledListItem>
            </Link>
          ))}
        {userRole === 'Super Admin' && (
          <>
            <StyledListItem onClick={handleManagementClick}>
              <IconWrapper>
                <img src="assets/icons/management.svg" alt="Management icon" />
              </IconWrapper>
              <Typography fontWeight="Bold" fontFamily={'Gabarito'}>
                Management
              </Typography>
              <DropdownIconWrapper>
                {openManagement ? <ExpandLess /> : <ExpandMore />}
              </DropdownIconWrapper>
            </StyledListItem>

            <Collapse in={openManagement} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {managementRoutes.map((route) => (
                  <Link
                    to={route.path}
                    key={route.path}
                    style={{ textDecoration: 'none' }}
                  >
                    <StyledListItem
                      sx={{ pl: 4 }}
                      isactive={location.pathname === route.path}
                    >
                      <IconWrapper>
                        <img src={route.icon} alt={`${route.name} icon`} />
                      </IconWrapper>
                      <Typography fontFamily={'Gabarito'}>
                        {route.name}
                      </Typography>
                      {location.pathname === route.path && <CircleDot />}
                    </StyledListItem>
                  </Link>
                ))}
              </List>
            </Collapse>
          </>
        )}

        {/* {remainingRoutes.map((route, index) => (
          <Link to={route.path} key={index} style={{ textDecoration: 'none' }}>
            <StyledListItem isactive={location.pathname === route.path}>
              <IconWrapper>
                <img src={route.icon} alt={`${route.name} icon`} />
              </IconWrapper>
              <Typography fontFamily={'Gabarito'}>{route.name}</Typography>
              {location.pathname === route.path && <CircleDot />}
            </StyledListItem>
          </Link>
        ))} */}
        {remainingRoutes.map((route, index) => {
          const isLogout = route.name === 'Logout'

          const handleLogoutClick = () => {
            window.location.href =
              'https://3sportal.yamahamotorphilippines.com.ph/AdvancedServiceKiosks/Home'
          }

          return isLogout ? (
            <React.Fragment key={route.path}>
              {(userRole === 'Maintenance Admin' ||
                userRole === 'Setup Area Supervisor' ||
                userRole === 'Super Admin') && (
                <StyledListItem
                  onClick={handleSync}
                  sx={{
                    cursor: 'pointer',
                    '& svg': {
                      color: '#000000',
                    },
                    '&:hover svg': {
                      filter:
                        'invert(33%) sepia(51%) saturate(2878%) hue-rotate(222deg) brightness(100%) contrast(80%)',
                    },
                  }}
                >
                  <IconWrapper>
                    <Sync />
                  </IconWrapper>
                  <Typography fontFamily={'Gabarito'}>Sync</Typography>
                </StyledListItem>
              )}
              <StyledListItem
                key={route.path}
                onClick={handleLogoutClick}
                sx={{ cursor: 'pointer' }}
              >
                <IconWrapper>
                  <img src={route.icon} alt={`${route.name} icon`} />
                </IconWrapper>
                <Typography fontFamily={'Gabarito'}>{route.name}</Typography>
              </StyledListItem>
            </React.Fragment>
          ) : (
            <Link
              to={route.path}
              key={route.path}
              style={{ textDecoration: 'none' }}
            >
              <StyledListItem isactive={location.pathname === route.path}>
                <IconWrapper>
                  <img src={route.icon} alt={`${route.name} icon`} />
                </IconWrapper>
                <Typography fontFamily={'Gabarito'}>{route.name}</Typography>
                {location.pathname === route.path && <CircleDot />}
              </StyledListItem>
            </Link>
          )
        })}
      </MenuStyles>
      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        open={alertRest.showAlert}
        onClose={alertRest.handleCloseAlert}
        autoHideDuration={3000}
      >
        <Alert
          onClose={alertRest.handleCloseAlert}
          elevation={6}
          variant="filled"
          severity={alertRest.alertSeverity}
        >
          {alertRest.alertMessage}
        </Alert>
      </Snackbar>
    </DrawerContainer>
  )
}
